import { useSkeem } from "../providers/SkeemProvider"
import React, { useCallback, useContext, useMemo } from "react"
import { joinUrl } from "../utils/joinUrl"
import { IRecord } from "../types/schema"
import { FlowResultFileApprovalStatuses } from "../pages/user/UserResultsShowPage/FlowResultFileApprovalStatuses"
import {
  IRotaDepartment,
  IRotaEmployee,
  IRotaItem,
  IRotaShiftType,
} from "../pages/user/Rota/rotaUtils"

export const ApiContext = React.createContext<IApiContext | null>(null)

export function useApi() {
  const value = useContext(ApiContext)
  if (!value) {
    throw new Error("Must use ApiProvider")
  }
  return value
}

export interface IBillingInfo {
  hasFreeAccount: boolean
  canAccessSystem: boolean

  /** If false, prompt for billing info (name, email and address) and ideally call updateCompanyBillingDetails before making subscription changes */
  validForStripeAccount: boolean
  /** True if the company has an existing billing account */
  hasStripeAccount: boolean
  /** True if the company has an active subscription, hasSubscription && !hasActiveSubscription implies a payment issue */
  hasSubscription: boolean
  /** True if the company should have access to the platform (they are paying their subscription) */
  hasActiveSubscription: boolean
  /** True if they have a sub that is incomplete_expired | canceled | unpaid */
  hadASubscription: boolean
  /** True if the data saved in our database doesn't match the data saved on Stripe. Call updateCompanySubscription to fix. */
  subscriptionOutdated: boolean
  /** Date that the current subscription will end */
  subscriptionWillCancel: string | null
  /** If present and in the future, the subscription will have a trial period until this date */
  trialEndDate: string | null
  /** When the next payment will be due */
  nextPaymentDate: string | null
  subscriptionBegan: string | null
  paymentMethod: {
    id: string
    type: string
    brand: string | null
    last4: string | null
    expires: string | null
  } | null
}

export interface IInvoice {
  id: string
  date: Date
  amountPennies: number
  paid: boolean
  pdfLink: string | null
}

export interface IInvoiceWithCompany extends IInvoice {
  company: IRecord<"companies", "name"> | null
}

export interface ISubscription {
  id: string
  date: string
  amountPennies: number
  company: IRecord<"companies", "name"> | null
  frequency: string
  daysUntilDue: number | null
  subscriptionWillCancel: string | null
  nextPaymentDate: string | null
}

export interface ISsoConfiguration {
  id: string
  providerType: "SAML" | "OIDC"
  issuerUrl: string | null
  entryPoint: string | null
  publicCert: string | null
  domainWhitelist: string | null
  isActive: boolean
  enableJit: boolean
  jitNotificationEmail: string | null
}

export type FourthEmployee = {
  CompnayName: string
  DateOfBirth: string
  EmployeeID: number
  FirstName: string
  LastName: string
  PayrollNumber: string
  Title: string
}

export type FourthAbsence = {
  EmployeeNumber: string
  EmployeeID: number
  Reference: string
  Type: string
  Days: number
  StartDate: string
  EndDate: string
  Status: string
  Paid: "Y" | "N" | "Yes" | "No"
  Reason: string
  ExpectedReturn: string
  ExpectedReturnDate: string
  MaternityDueDate: string
  MaternityDeliveryDate: string
  Documentation: string
}

export interface IApiContext {
  ssoLogin(data: {
    email: string
    returnTo?: string
  }): Promise<
    { type: "password" } | { type: "ssoRedirect"; url: string } | { type: "error"; message: string }
  >
  getCompanySsoConfiguration(
    companyId: string,
  ): Promise<{ configuration: ISsoConfiguration | null }>
  saveCompanySsoConfiguration(data: {
    companyId: string
    issuerUrl: string
    entryPoint: string
    publicCert: string
    domainWhitelist: string
    isActive: boolean
    enableJit: boolean
    jitNotificationEmail: string
  }): Promise<{ configuration: ISsoConfiguration }>
  initCompanySsoConfigurationTest(data: {
    companyId: string
    issuerUrl: string
    entryPoint: string
    publicCert: string
    email?: string
  }): Promise<{ url: string }>
  signIn(
    data:
      | { type: "email"; email: string; password: string; otpCode?: string }
      | {
          type: "payroll"
          payrollNumber: string
          companyPrefix: string
          password: string
          otpCode?: string
        },
  ): Promise<
    | { type: "success"; token: string }
    | { type: "error"; message: string }
    | { type: "requiresOTP"; emailSentTo: string }
  >
  getMicrosoftAuthStatus(): Promise<{
    connected: boolean
    email?: string
    lastSync?: string
  }>
  getMicrosoftAuthUrl(): Promise<{ url: string }>
  disconnectMicrosoft(): Promise<{ success: true }>
  transcribe(transcribeId: string): Promise<{ acknowledged: true }>

  canConvertToDocx(fileId: string): Promise<boolean>

  convertToDocx(fileId: string): Promise<string>

  sendErrorPageReport(data: {
    reason: string
    url: string
    error: string
    context?: {
      user?: { id?: string; name?: string; email?: string } | null
      company?: { id?: string; name?: string } | null
      siteLoadedAt?: string | null
      occurredAt?: string | null
      userAgent?: string | null
      componentStack?: string | null
    }
  }): Promise<{ ok: true }>
  sendFeedbackEmail(feedbackId: string): Promise<{ ok: true }>

  sendHelpRequestEmail(helpRequestId: string): Promise<{ ok: true }>
  sendHelpRequestMessage(data: {
    helpRequestId: string
    messageId: string
  }): Promise<{ ok: true; error?: never } | { error: string; ok?: never }>

  sendCaseFileEmail(data: {
    flowResultId: string
    flowResultFileIds: string[]
    subject: string
    message: string
    employeeIds: string[]
  }): Promise<{ ok: true; error?: never } | { error: string; ok?: never }>

  /** Fetch a list of all invoices for all customers */
  getAllSubscriptions(opts?: {
    startingAfter?: string
    endingBefore?: string
  }): Promise<{ subscriptions: ISubscription[]; hasMore: boolean }>

  /** Fetch a list of all invoices for all customers */
  getAllInvoices(opts?: {
    startingAfter?: string
    endingBefore?: string
  }): Promise<{ invoices: IInvoiceWithCompany[]; hasMore: boolean }>

  /** Fetch high-level information about the company's billing status */
  getCompanyBillingInfo(companyId: string): Promise<IBillingInfo>

  /** Fetch a list of invoices, including the next upcoming one */
  getCompanyInvoices(companyId: string): Promise<IInvoice[]>

  /** Push any new company details to Stripe */
  updateCompanyBillingDetails(companyId: string): Promise<{ ok: true }>

  /** Assign a payment method to this company (after creating it from card details on the front) */
  setCompanyPaymentMethod(companyId: string, paymentMethodId: string): Promise<{ ok: true }>

  /** Push any new plan/pricing details to Stripe, starts a subscription if one doesn't exist */
  updateCompanySubscription(companyId: string): Promise<{ ok: true }>

  /** Cancel the current subscription (use updateSubscription to start it again) */
  cancelCompanySubscription(companyId: string): Promise<{ ok: true }>

  /** Cancel the current subscription immediately */
  deleteCompanySubscription(companyId: string): Promise<{ ok: true }>

  /** Cancel the current subscription (use updateSubscription to start it again) */
  payForCompanyHrJob(companyId: string, hrJobId: string): Promise<{ ok: true }>

  createStripeSetupIntent(companyId: string): Promise<{ setupIntentClientSecret: string }>

  flowFileNotifyUsers(flowFileId: string, userIds: string[]): Promise<{ ok: true }>

  companyNotifySkippedFiles(data: {
    userId: string
    fileIds: string[]
    resultId: string
  }): Promise<{ ok: true }>
  actionTagNotifyForUser(
    tagId: string,
    userId: string,
    origin: string,
    triggerRecord?: { model: string; id: string },
  ): Promise<{ ok: true }>
  actionTagSkippedFile(opts: {
    userId: string
    fileIds: string[]
  }): Promise<{ ok: true } | { error: string }>

  emailPreviews(data?: { asJson?: boolean }): Promise<{
    emailSentbyAndHtmls: Record<string, { sentBy: string; html: string } | null>
  }>

  newCompanyNotification(companyId: string): Promise<{ ok: true }>

  emailJobRequestDeclined(jobId: string): Promise<{ ok: true }>

  emailJobRequestAccepted(jobId: string): Promise<{ ok: true }>

  emailWorkRequestCreated(jobId: string): Promise<{ ok: true }>

  emailJobRequestDetails(jobId: string, message: string): Promise<{ ok: true }>

  sendCaseInvite(
    caseId: string,
    users: { id: string }[],
    message?: string,
  ): Promise<
    | { success: true; detail: { sent: boolean; email: string }[] }
    | { success: false; error: string }
  >
  sendCasePromotedPrimary(
    caseId: string,
    userId: string,
  ): Promise<
    | { success: true; detail: { sent: boolean; email: string }[] }
    | { success: false; error: string }
  >
  passwordUpdateNotification(opts: {
    userId: string
    password?: string
  }): Promise<{ error: boolean; message: string }>
  flowResultFileRequestApproval(
    flowResultFileId: string,
  ): Promise<{ error: boolean; message: string }>
  flowResultFileRequestUpload(
    flowResultFileId: string,
    opts?: { useSupportUsers?: boolean },
  ): Promise<{ error: boolean; message: string }>
  flowResultFileApproveFile(flowResultFileId: string): Promise<{ error: boolean; message: string }>
  flowResultFileDenyFile(flowResultFileId: string): Promise<{ error: boolean; message: string }>
  flowResultEmailAssessor(
    flowResultId: string,
    assessorEmail: string,
    message: string,
  ): Promise<{ error: boolean; message: string }>
  notifyOfNewFileRequest(data: { flowResultFileId: string }): Promise<void>
  notifyOfApprovedFile(data: { flowResultFileId: string; autoAttachFile?: boolean }): Promise<void>
  notifyOfRequestedChangesFile(data: { flowResultFileId: string }): Promise<void>
  notifyOfDeniedFile(data: { flowResultFileId: string }): Promise<void>
  notifyOfForwardedFile(data: { flowResultFileId: string }): Promise<void>
  notifyOfAmendedFileRequest(data: {
    flowResultFileId: string
    previousState: FlowResultFileApprovalStatuses
  }): Promise<void>
  /** Unified notify entry — routes to editor or downloadable backend by kind. */
  notifyFlowResultFile(data: {
    action:
      | "submitted"
      | "approved"
      | "denied"
      | "requestedChanges"
      | "forwarded"
      | "amended"
      | "requestApproval"
      | "requestUpload"
    flowResultFileId: string
    kind: "editor" | "downloadable"
    autoAttachFile?: boolean
    useSupportUsers?: boolean
    previousState?: FlowResultFileApprovalStatuses
  }): Promise<void>
  alertUsersOfSpecialFlowAccess(data: { flowId: string; userIds: string[] }): Promise<void>
  generateLoginTokenForUser(data: { userId: string }): Promise<{ token: string }>

  notifyNewReminderCreated(data: { reminderId: string }): Promise<void>

  // Rota routes
  rotaItems(params: {
    companyId: string
    departmentIds?: string[]
    employeeIds?: string[]
    startDate?: Date
    endDate?: Date
    view?: "all" | "rota" | "holidays"
  }): Promise<IRotaItem[]>
  rotaDepartments(params: {
    companyId: string
    employeeId?: string
    employeeIds?: string[]
    departmentId?: string
  }): Promise<IRotaDepartment[]>
  rotaEmployees(params: { companyId: string; employeeIds?: string[] }): Promise<IRotaEmployee[]>
  rotaShiftTypes(params: { companyId: string }): Promise<IRotaShiftType[]>
  rotaHolidayConsumed(params: {
    companyId: string
    employeeIds: string[]
  }): Promise<{ employeeId: string; used: number }[]>
  notificationsSend(data: {
    heading?: string
    body: string
    route?: string
    userId: string
  }): Promise<{ saved: true; record: IRecord<"notifications"> }>
  notifyConcernChange(data: {
    resultId: string
    oldConcern: string
    newConcern: string
  }): Promise<{ ok: true }>
  authorizeHeartbeat(data: string): Promise<{ authorized: boolean }>
  generateBlogWithAi(data: {
    name: string
  }): Promise<{ name: string; teaser: string; body: string }>
  generateBlogWithAiRequestChanges(data: {
    changes: string
    name: string
    teaser: string
    body: string
  }): Promise<{ name: string; teaser: string; body: string }>
  sendProbationReminderEmail(
    employeeId: string,
    data: { toEmail: string; subject: string; bodyText: string },
  ): Promise<{ ok: boolean }>
  sendEmployeeInvite(
    employeeId: string,
    data: { toEmail: string; updateContactEmail?: boolean },
  ): Promise<{ ok: boolean }>
  validateInviteEmail(data: {
    email: string
    excludeEmployeeId?: string
  }): Promise<{ isUnique: boolean }>
  sendEmployeePasswordReset(employeeId: string): Promise<{ ok: boolean }>
  validateEmployeeInviteToken(data: {
    token: string
  }): Promise<{ employee: { id: string; firstName: string; lastName: string; email: string } }>
  createEmployeeUser(data: { token: string; password: string }): Promise<{ authToken: string }>
  getCompanyIdForResult(data: { resultId: string }): Promise<{ companyId: string | null }>
  register(data: {
    userData: {
      email: string
      password: string
      firstName: string
      lastName: string
    }
    companyData: {
      name: string
      phoneNumber: string
      email: string
      addressLine1: string
      addressLine2: string
      addressLine3: string
      addressCity: string
      addressCounty: string
      addressPostcode: string
      charityNumber: string
    }
  }): Promise<{ authToken: string }>
  getStripeDashboardLink(data: { companyId: string; returnTo?: string }): Promise<{ url: string }>
  getStripeSubscriptionDetails(data: { companyId: string }): Promise<null | {
    id: string
    interval: string
    taxRate: number
    items: { id: string; name: string; quantity: number; exVat: number; pricePennies: number }[]
    exVat: number
    totalPennies: number
    _raw: unknown
  }>
  syncStripeCompanyDetails(data: { companyId: string }): Promise<void>
  fourthTestCredentials(data: {
    organisationId: string
    username: string
    password: string
  }): Promise<{ success: boolean; message: string }>
  fourthGetEmployees(data: {
    companyId: string
  }): Promise<{ success: boolean; employees: FourthEmployee[] }>
  fourthGetAbsences(data: {
    companyId: string
  }): Promise<{ success: boolean; absences: FourthAbsence[] }>
  fourthImportAll(data: { companyId: string }): Promise<{
    success: boolean
    employees: { created: number; updated: number; totalRemote: number }
    absences: { created: number; updated: number; totalRemote: number }
  }>
}

declare global {
  interface Window {
    api: IApiContext
  }
}

export function ApiProvider({ children }: { children: React.ReactNode }) {
  const skeem = useSkeem()
  const apiRequest = useCallback(
    async (url: string, body: any = {}) => {
      const headers: Record<string, string> = {}

      headers["content-type"] = "application/json"

      const authToken = await skeem.getAuthToken()

      if (authToken) {
        headers["X-skeem-auth"] = authToken
      }

      const fullUrl = joinUrl(`/api`, url)
      const response = await fetch(fullUrl, {
        method: "POST",
        headers,
        body: JSON.stringify(body),
      })

      const json = await response.json()

      if (json.error) {
        throw json.error
      }

      return json
    },
    [skeem],
  )

  const value: IApiContext = useMemo(
    () => ({
      async ssoLogin(data) {
        const response = await fetch("/api/auth/sso/login", {
          method: "POST",
          headers: { "content-type": "application/json" },
          body: JSON.stringify(data),
        })
        return response.json()
      },
      async getCompanySsoConfiguration(companyId) {
        const authToken = await skeem.getAuthToken()
        const headers: Record<string, string> = { "content-type": "application/json" }
        if (authToken) {
          headers["X-skeem-auth"] = authToken
        }
        const response = await fetch("/api/auth/sso/configuration", {
          method: "POST",
          headers,
          body: JSON.stringify({ companyId }),
        })
        return response.json()
      },
      async saveCompanySsoConfiguration(data) {
        const authToken = await skeem.getAuthToken()
        const headers: Record<string, string> = { "content-type": "application/json" }
        if (authToken) {
          headers["X-skeem-auth"] = authToken
        }
        const response = await fetch("/api/auth/sso/configuration/save", {
          method: "POST",
          headers,
          body: JSON.stringify(data),
        })
        return response.json()
      },
      async initCompanySsoConfigurationTest(data) {
        const authToken = await skeem.getAuthToken()
        const headers: Record<string, string> = { "content-type": "application/json" }
        if (authToken) {
          headers["X-skeem-auth"] = authToken
        }
        const response = await fetch("/api/auth/sso/configuration/test/init", {
          method: "POST",
          headers,
          body: JSON.stringify(data),
        })
        return response.json()
      },
      signIn: data => apiRequest("/signIn", data),
      getMicrosoftAuthStatus() {
        return apiRequest("/microsoft-auth-status")
      },
      getMicrosoftAuthUrl() {
        return apiRequest("/microsoft-auth-url")
      },
      disconnectMicrosoft() {
        return apiRequest("/microsoft-auth-disconnect")
      },
      async canConvertToDocx(fileId) {
        const response = await apiRequest(`/canConvertToDocx/${fileId}`)
        return response.canConvert
      },
      emailPreviews(data?: { startAsap?: boolean; asJson?: boolean }) {
        return apiRequest(`/email-previews`, data)
      },
      async convertToDocx(fileId) {
        const response = await apiRequest(`/convertToDocx/${fileId}`)
        return response.fileId
      },
      transcribe(transcribeId) {
        return apiRequest(`/files/${transcribeId}/transcribe`)
      },
      sendErrorPageReport(details) {
        return apiRequest("/error-page-report", details)
      },
      sendFeedbackEmail(feedbackId) {
        return apiRequest("/feedback-notify", { feedbackId })
      },
      sendHelpRequestEmail(helpRequestId) {
        return apiRequest("/sendHelpRequestEmail", { helpRequestId })
      },
      sendHelpRequestMessage(data) {
        return apiRequest(`/sendHelpRequestMessage`, data)
      },
      sendCaseFileEmail(data) {
        return apiRequest(`/sendCaseFileEmail`, data)
      },
      sendProbationReminderEmail(employeeId, data) {
        return apiRequest(`/employee/${employeeId}/send-probation-reminder`, data)
      },
      sendEmployeeInvite(employeeId, data) {
        return apiRequest(`/employeeInvitesSendInvite`, { employeeId, ...data })
      },
      validateInviteEmail(data) {
        return apiRequest(`/employeeInvitesValidateEmail`, data)
      },
      sendEmployeePasswordReset(employeeId) {
        return apiRequest(`/employeeInvitesSendPasswordReset`, { employeeId })
      },
      validateEmployeeInviteToken(data) {
        return apiRequest(`/employeeInvitesValidateInvite`, data)
      },
      createEmployeeUser(data) {
        return apiRequest(`/employeeInvitesCreateUser`, data)
      },
      getCompanyBillingInfo(companyId) {
        return apiRequest(`/billing/${companyId}/info`)
      },
      async getCompanyInvoices(companyId) {
        const response = await apiRequest(`/billing/${companyId}/invoices`)
        return response.invoices
      },
      async getAllSubscriptions(opts) {
        const response = await apiRequest(`/billing/subscriptions`, opts)
        return response.subscriptions
      },
      async getAllInvoices(opts = {}) {
        const response = await apiRequest(`/billing/invoices`, opts)
        return response.data
      },
      updateCompanyBillingDetails(companyId) {
        return apiRequest(`/billing/${companyId}/updateDetails`)
      },
      createStripeSetupIntent(companyId) {
        return apiRequest(`/billing/${companyId}/createSetupIntent`)
      },
      setCompanyPaymentMethod(companyId, paymentMethodId) {
        return apiRequest(`/billing/${companyId}/setPaymentMethod`, {
          paymentMethodId,
        })
      },
      updateCompanySubscription(companyId) {
        return apiRequest(`/billing/${companyId}/updateSubscription`)
      },
      cancelCompanySubscription(companyId) {
        return apiRequest(`/billing/${companyId}/cancelSubscription`)
      },
      deleteCompanySubscription(companyId) {
        return apiRequest(`/billing/${companyId}/deleteSubscription`)
      },
      payForCompanyHrJob(companyId, hrJobId) {
        return apiRequest(`/billing/${companyId}/payForHrJob`, { hrJobId })
      },
      flowFileNotifyUsers(flowFileId, userIds) {
        return apiRequest(`/flowFiles/${flowFileId}/notifyUsers`, {
          userIds,
        })
      },
      companyNotifySkippedFiles(opts) {
        return apiRequest(`/company/skippedFile`, opts)
      },
      actionTagNotifyForUser(tagId, userId, origin, triggerRecord) {
        return apiRequest(`/actionTags/${tagId}/notifyForUser`, {
          userId,
          origin,
          sourceUrl: window.location.href,
          triggerRecord,
        })
      },
      actionTagSkippedFile(opts) {
        return apiRequest(`/actionTags/skippedFile`, opts)
      },
      newCompanyNotification(companyId) {
        return apiRequest(`/company/${companyId}/newCompanyNotification`)
      },
      emailJobRequestDeclined(jobId) {
        return apiRequest(`/job-request/${jobId}/declined`)
      },
      emailJobRequestAccepted(jobId) {
        return apiRequest(`/job-request/${jobId}/accept`)
      },
      emailWorkRequestCreated(jobId) {
        return apiRequest(`/job-request/${jobId}/created`)
      },
      emailJobRequestDetails(jobId, message) {
        return apiRequest(`/job-request/${jobId}/details`, { message })
      },
      sendCaseInvite(flowResultId: string, users: { id: string }[], message?: string) {
        return apiRequest(`/case-invite/${flowResultId}`, { users, message })
      },
      sendCasePromotedPrimary(flowResultId: string, userId: string) {
        return apiRequest(`/case-invite/${flowResultId}/primary`, { userId })
      },
      passwordUpdateNotification({ userId, password }: { userId: string; password?: string }) {
        return apiRequest(`/password-update`, { userId, password })
      },
      flowResultFileRequestApproval(id) {
        return apiRequest(`/flow-result-file/request-approval`, {
          flowResultFileId: id,
        })
      },
      flowResultFileRequestUpload(id, opts?: { useSupportUsers: boolean }) {
        return apiRequest(`/flow-result-file/request-upload`, {
          flowResultFileId: id,
          ...(opts ?? {}),
        })
      },
      flowResultFileApproveFile(id) {
        return apiRequest(`/flow-result-file/approve-file`, {
          flowResultFileId: id,
        })
      },
      flowResultFileDenyFile(id) {
        return apiRequest(`/flow-result-file/deny-file`, { flowResultFileId: id })
      },
      notifyOfNewFileRequest(data) {
        return apiRequest(`/notifyOfNewFileRequest`, data)
      },
      notifyOfApprovedFile(data) {
        return apiRequest(`/notifyOfApprovedFile`, data)
      },
      notifyOfRequestedChangesFile(data) {
        return apiRequest(`/notifyOfRequestedChangesFile`, data)
      },
      notifyOfDeniedFile(data) {
        return apiRequest(`/notifyOfDeniedFile`, data)
      },
      notifyOfForwardedFile(data) {
        return apiRequest(`/notifyOfForwardedFile`, data)
      },
      notifyOfAmendedFileRequest(data) {
        return apiRequest(`/notifyOfAmendedFileRequest`, data)
      },
      async notifyFlowResultFile(data) {
        const { action, flowResultFileId, kind, autoAttachFile, useSupportUsers, previousState } =
          data
        switch (action) {
          case "submitted":
            return apiRequest(`/notifyOfNewFileRequest`, { flowResultFileId })
          case "approved":
            if (kind === "downloadable") {
              return apiRequest(`/flow-result-file/approve-file`, { flowResultFileId })
            }
            return apiRequest(`/notifyOfApprovedFile`, { flowResultFileId, autoAttachFile })
          case "denied":
            if (kind === "downloadable") {
              return apiRequest(`/flow-result-file/deny-file`, { flowResultFileId })
            }
            return apiRequest(`/notifyOfDeniedFile`, { flowResultFileId })
          case "requestedChanges":
            return apiRequest(`/notifyOfRequestedChangesFile`, { flowResultFileId })
          case "forwarded":
            return apiRequest(`/notifyOfForwardedFile`, { flowResultFileId })
          case "amended":
            return apiRequest(`/notifyOfAmendedFileRequest`, {
              flowResultFileId,
              previousState,
            })
          case "requestApproval":
            return apiRequest(`/flow-result-file/request-approval`, { flowResultFileId })
          case "requestUpload":
            return apiRequest(`/flow-result-file/request-upload`, {
              flowResultFileId,
              ...(useSupportUsers !== undefined ? { useSupportUsers } : {}),
            })
          default:
            return
        }
      },
      async flowResultEmailAssessor(flowResultId: string, assessorEmail: string, message: string) {
        const response = await apiRequest(`/flow-result-message`, {
          id: flowResultId,
          email: assessorEmail,
          message,
        })
        if (response.error) {
          throw new Error(response.error)
        }
        return response
      },
      async alertUsersOfSpecialFlowAccess(data) {
        return apiRequest(`/alertUsersOfSpecialFlowAccess`, data)
      },
      // Rota routes
      rotaItems(data) {
        return apiRequest("/rota/items", data)
      },
      rotaDepartments(data) {
        return apiRequest("/rota/departments", data)
      },
      rotaEmployees(data) {
        return apiRequest("/rota/employees", data)
      },
      rotaShiftTypes(data) {
        return apiRequest("/rota/shift-types", data)
      },
      rotaHolidayConsumed(data) {
        return apiRequest(`/rota/holiday-consumed`, data)
      },
      generateLoginTokenForUser(data) {
        return apiRequest("/generateLoginTokenForUser", data)
      },
      notifyNewReminderCreated(data) {
        return apiRequest("/notifyNewReminderCreated", data)
      },
      notificationsSend(data) {
        return apiRequest("/notifications/send", data)
      },
      notifyConcernChange(data: { resultId: string; oldConcern: string; newConcern: string }) {
        return apiRequest("/flowResults/notifyConcernChange", data)
      },
      authorizeHeartbeat(data) {
        return apiRequest("/authorize-heartbeat", { sid: data })
      },
      generateBlogWithAi(data) {
        return apiRequest("/generateBlogWithAi", data)
      },
      generateBlogWithAiRequestChanges(data) {
        return apiRequest("/generateBlogWithAiRequestChanges", data)
      },
      getCompanyIdForResult(data) {
        return apiRequest(`/getCompanyIdForResult`, data)
      },
      register(data) {
        return apiRequest("/register", data)
      },
      getStripeDashboardLink(data) {
        return apiRequest("/getStripeDashboardLink", data)
      },
      async getStripeSubscriptionDetails(data) {
        const response = await apiRequest("/getStripeSubscriptionDetails", data)
        return response.data
      },
      syncStripeCompanyDetails(data) {
        return apiRequest("/syncStripeCompanyDetails", data)
      },
      fourthTestCredentials(data) {
        return apiRequest("/fourthTestCredentials", data)
      },
      fourthGetEmployees(data) {
        return apiRequest("/fourthGetEmployees", data)
      },
      fourthGetAbsences(data) {
        return apiRequest("/fourthGetAbsences", data)
      },
      fourthImportAll(data) {
        return apiRequest("/fourthImportAll", data)
      },
    }),
    [],
  )

  window.api = value
  return <ApiContext.Provider value={value}>{children}</ApiContext.Provider>
}
